import { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { useGuestId } from '../hooks/useGuestId';
import { getRoom, joinRoom, getRoomLeaderboard } from '../services/roomApi';
import { formatTime } from '../utils/formatters';

function JoinRoom() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  // Ensure guest ID is generated for non-authenticated users
  useGuestId();

  const [room, setRoom] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isJoining, setIsJoining] = useState(false);
  const [error, setError] = useState(null);
  const [guestName, setGuestName] = useState('');
  const [sortBy, setSortBy] = useState('moves'); // 'time' or 'moves'

  useEffect(() => {
    const fetchRoom = async () => {
      try {
        setIsLoading(true);
        const roomData = await getRoom(code);
        setRoom(roomData);

        // Fetch leaderboard if enabled
        if (roomData.settings.showLeaderboard) {
          const leaderboardData = await getRoomLeaderboard(code);
          setLeaderboard(leaderboardData);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRoom();
  }, [code]);

  const handleJoin = async () => {
    try {
      setIsJoining(true);
      // Pass guest name if user is not logged in
      const displayName = !user ? (guestName.trim() || 'Guest') : null;
      await joinRoom(code, displayName);
      // Navigate to play the puzzle
      // eslint-disable-next-line no-underscore-dangle
      navigate(`/play/${room.puzzle._id}?room=${code}`);
    } catch (err) {
      setError(err.message);
      setIsJoining(false);
    }
  };

  const handlePlayAgain = () => {
    // eslint-disable-next-line no-underscore-dangle
    navigate(`/play/${room.puzzle._id}?room=${code}`);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-puzzle-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-puzzle-blue" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-puzzle-background flex items-center justify-center p-4">
        <div className="bg-puzzle-surface rounded-2xl shadow-xl p-8 max-w-md w-full text-center border border-puzzle-border">
          <div className="text-6xl mb-4">😕</div>
          <h1 className="text-2xl font-bold text-puzzle-text mb-2">Room Not Found</h1>
          <p className="text-puzzle-text-secondary mb-6">{error}</p>
          <Link
            to="/"
            className="inline-block bg-puzzle-blue text-white px-6 py-3 rounded-full font-medium hover:bg-blue-600 transition-colors"
          >
            Go Home
          </Link>
        </div>
      </div>
    );
  }

  const {
    puzzle, creator, name, settings, participantCount, isParticipant, participantData,
  } = room;
  const hasCompleted = participantData?.completedAt;

  return (
    <div className="min-h-screen bg-puzzle-background py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Room Header */}
        <div className="bg-puzzle-surface rounded-2xl shadow-lg p-6 mb-6 border border-puzzle-border">
          <div className="flex flex-col items-center text-center">
            {/* Logo */}
            <Link to="/" className="mb-4">
              <img
                src="/images/logo.png"
                alt="Puzzlists"
                className="h-24"
              />
            </Link>

            <h1 className="text-2xl font-bold text-puzzle-text mb-2">{name}</h1>

            {creator && (
              <p className="text-puzzle-text-secondary mb-4">
                Created by
                {' '}
                <Link to={`/profile/${creator.username}`} className="text-puzzle-blue hover:underline">
                  {creator.displayName || creator.username}
                </Link>
              </p>
            )}

            {/* Puzzle Info */}
            <div className="flex items-center gap-4 text-sm text-puzzle-text-secondary mb-6">
              <span className="flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                </svg>
                {puzzle.config.rows * puzzle.config.columns}
                {' '}
                pieces
              </span>
              <span className="flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                {participantCount}
                {' '}
                players
              </span>
            </div>

            {/* Action Button */}
            {hasCompleted && (
              <div className="text-center">
                <div className="text-puzzle-green font-semibold text-lg mb-2">
                  You completed this puzzle!
                </div>
                <p className="text-sm text-puzzle-text-secondary">
                  Time:
                  {' '}
                  {formatTime(participantData.stats.playTime)}
                  {' '}
                  • Moves:
                  {' '}
                  {participantData.stats.moveCount}
                </p>
              </div>
            )}
            {!hasCompleted && isParticipant && (
              <button
                type="button"
                onClick={handlePlayAgain}
                className="bg-puzzle-blue text-white px-8 py-3 rounded-full font-medium hover:bg-blue-600 transition-colors"
              >
                Continue Puzzle
              </button>
            )}
            {!hasCompleted && !isParticipant && (
              <>
                {/* Guest name input - only show for non-authenticated users */}
                {!user && (
                  <div className="mb-4 w-full max-w-xs">
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <label className="block">
                      <span className="text-sm text-puzzle-text-secondary mb-2 block">
                        Enter your name
                      </span>
                      <input
                        type="text"
                        value={guestName}
                        onChange={(e) => setGuestName(e.target.value)}
                        placeholder="Your name"
                        className="w-full px-4 py-2 border border-puzzle-border rounded-lg bg-puzzle-surface text-puzzle-text focus:outline-none focus:ring-2 focus:ring-puzzle-blue"
                        maxLength={30}
                      />
                    </label>
                  </div>
                )}
                <button
                  type="button"
                  onClick={handleJoin}
                  disabled={isJoining}
                  className="bg-puzzle-blue text-white px-8 py-3 rounded-full font-medium hover:bg-blue-600 transition-colors disabled:opacity-50"
                >
                  {isJoining ? 'Joining...' : 'Start Puzzle'}
                </button>
              </>
            )}

            {/* Guest notice */}
            {!user && settings.allowGuests && (
              <p className="mt-4 text-sm text-puzzle-text-secondary">
                Playing as guest.
                {' '}
                <Link to="/login" className="text-puzzle-blue hover:underline">
                  Sign in
                </Link>
                {' '}
                to save your progress.
              </p>
            )}
          </div>
        </div>

        {/* Leaderboard */}
        {settings.showLeaderboard && leaderboard && (
          <div className="bg-puzzle-surface rounded-2xl shadow-lg p-6 border border-puzzle-border">
            <h2 className="text-lg font-semibold text-puzzle-text mb-4">
              Leaderboard
              <span className="ml-2 text-sm font-normal text-puzzle-text-secondary">
                (
                {leaderboard.totalCompletions}
                {' '}
                completed)
              </span>
            </h2>

            {leaderboard.leaderboard.length > 0 ? (
              <>
                {/* Sort Toggle */}
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-sm text-puzzle-text-secondary">Sort by:</span>
                  <button
                    type="button"
                    onClick={() => setSortBy('moves')}
                    className={`px-3 py-1 text-sm rounded-full transition-colors ${
                      sortBy === 'moves'
                        ? 'bg-puzzle-blue text-white'
                        : 'bg-puzzle-muted text-puzzle-text hover:bg-puzzle-hover'
                    }`}
                  >
                    Moves
                  </button>
                  <button
                    type="button"
                    onClick={() => setSortBy('time')}
                    className={`px-3 py-1 text-sm rounded-full transition-colors ${
                      sortBy === 'time'
                        ? 'bg-puzzle-blue text-white'
                        : 'bg-puzzle-muted text-puzzle-text hover:bg-puzzle-hover'
                    }`}
                  >
                    Time
                  </button>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-left text-puzzle-text-secondary border-b border-puzzle-border">
                      <tr>
                        <th className="py-2 px-3 w-12">Rank</th>
                        <th className="py-2 px-3">Player</th>
                        <th className={`py-2 px-3 text-right ${sortBy === 'time' ? 'text-puzzle-blue' : ''}`}>
                          Time
                        </th>
                        <th className={`py-2 px-3 text-right hidden sm:table-cell ${sortBy === 'moves' ? 'text-puzzle-blue' : ''}`}>
                          Moves
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...leaderboard.leaderboard]
                        .sort((a, b) => (sortBy === 'moves'
                          ? a.moveCount - b.moveCount
                          : a.playTime - b.playTime))
                        .map((player, index) => {
                          const rank = index + 1;
                          return (
                            <tr key={player.userId || player.guestId || index} className="border-b border-puzzle-border">
                              <td className="py-3 px-3">
                                {rank === 1 && '🥇'}
                                {rank === 2 && '🥈'}
                                {rank === 3 && '🥉'}
                                {rank > 3 && (
                                  <span className="text-puzzle-text-secondary">{rank}</span>
                                )}
                              </td>
                              <td className="py-3 px-3">
                                <div className="flex items-center gap-2">
                                  <div className="w-6 h-6 rounded-full bg-puzzle-muted overflow-hidden flex items-center justify-center flex-shrink-0">
                                    {player.avatar ? (
                                      <img
                                        src={player.avatar}
                                        alt={player.displayName}
                                        className="w-full h-full object-cover"
                                      />
                                    ) : (
                                      <span className="text-xs text-puzzle-text-secondary">
                                        {(player.displayName || 'G').slice(0, 1).toUpperCase()}
                                      </span>
                                    )}
                                  </div>
                                  <span className="text-puzzle-text">
                                    {player.displayName || 'Guest'}
                                    {player.isGuest && (
                                      <span className="ml-1 text-xs text-puzzle-text-secondary">(Guest)</span>
                                    )}
                                  </span>
                                </div>
                              </td>
                              <td className={`py-3 px-3 text-right font-medium ${sortBy === 'time' ? 'text-puzzle-blue' : 'text-puzzle-text-secondary'}`}>
                                {formatTime(player.playTime)}
                              </td>
                              <td className={`py-3 px-3 text-right hidden sm:table-cell ${sortBy === 'moves' ? 'text-puzzle-blue font-medium' : 'text-puzzle-text-secondary'}`}>
                                {player.moveCount}
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <p className="text-center text-puzzle-text-secondary py-4">
                No completions yet. Be the first!
              </p>
            )}

            {/* In Progress Players */}
            {leaderboard.inProgress.length > 0 && (
              <div className="mt-4 pt-4 border-t border-puzzle-border">
                <p className="text-sm text-puzzle-text-secondary mb-2">
                  Currently playing:
                  {' '}
                  {leaderboard.inProgress.map((p) => p.displayName || 'Guest').join(', ')}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default JoinRoom;
